import React from 'react';

import { Link } from 'utils/navigation/router';

import { SanityBlockContentLinkProps } from './types';

/**
 * This is a useful override for removing unwanted component hierarchy.
 * E.g. serializers.marks.em by default adds an <em /> wrapper around text content.
 * If you want to only render the container component and text, override
 * the serializers where serializers.marks.em = PassthroughComponent
 */
export const PassthroughComponent: React.FC = props => <>{props.children}</>;

const NON_DESCRIPTIVE_TEXTS = [
  'link',
  'here',
  'click here',
  'read more',
  'learn more',
  'learn more here',
];

function isNonDescriptiveText(text: string): boolean {
  const lower = text?.trim().toLowerCase();
  return (
    NON_DESCRIPTIVE_TEXTS.includes(lower) ||
    lower?.startsWith('http') ||
    lower?.startsWith('www.') ||
    /^[a-z]+\.(com|ca|org|net)\b/i.test(lower)
  );
}

/**
 * This function return a <Link /> (internal link) or a <a /> (external link) depending on the type of href added in sanity
 */
export const formatSanityLink = ({
  children,
  mark,
  fromApp,
  username,
  useremail,
  ...rest
}: SanityBlockContentLinkProps) => {
  const href = mark?.href;
  const isInternalLink = href?.startsWith('/');

  let url = href;
  const params = new URLSearchParams();
  if (fromApp) {
    params.append('fromApp', 'true');
  }
  if (username) {
    params.append('username', username);
  }
  if (useremail) {
    params.append('useremail', useremail);
  }

  const paramString = params.toString();
  if (paramString && href) {
    url = `${href}${href.includes('?') ? '&' : '?'}${paramString}`;
  }

  const linkText = Array.isArray(children) ? children.join('') : String(children ?? '');
  const ariaLabel = isNonDescriptiveText(linkText) ? href : undefined;

  return isInternalLink ? (
    <Link to={url} data-testid="sanity-link-to" data-url={url} aria-label={ariaLabel} {...rest}>
      {children}
    </Link>
  ) : (
    <a
      href={url}
      target={mark?.blank ? '_blank' : undefined}
      rel={mark?.blank ? 'noreferrer' : undefined}
      data-testid="sanity-a-href"
      aria-label={ariaLabel}
      {...rest}
    >
      {children}
    </a>
  );
};
